import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { NoiseOverlay, WaxSeal } from "@/components/luxury";

/**
 * ReelStage — locked-ratio cinematic stage for the marketing reels.
 *
 * Renders an exactly-1080×1920 (vertical) or 1920×1080 (horizontal) canvas
 * scaled to fit the viewport. Inside, children read a 0..1 progress value
 * via `useTimeline()` and drive their own animations off it. The stage
 * itself runs the clock, the loop reset, the gold seal hairline reveal,
 * and the closing WaxSeal + wordmark resolve so each reel only needs to
 * supply its 5 beats of content.
 *
 * URL flags
 *   • `?o=horizontal`   → 1920×1080 (YouTube/landing-page autoplay)
 *   • `?clean=1`        → hide the small "1080×1920 · 15s" debug pill
 *   • `?paused=1&t=0.4` → freeze on a specific progress for stills
 *
 * Why the inner box is exactly 1080×1920 px:
 *   The user records the reel via OS screen recorder. A locked pixel
 *   canvas means whatever the viewport size, the captured frame can be
 *   center-cropped to the full canvas at 1:1 pixel ratio — no scaling
 *   blur. The CSS `transform: scale(...)` is purely viewport-fitting.
 */

const TimelineCtx = createContext({ t: 0, cycle: 0, orientation: "vertical" });
export const useTimeline = () => useContext(TimelineCtx);

/** Convenience: returns 0..1 progress between `from` and `to` (clamped). */
export const beatProgress = (t, from, to) => {
  if (t <= from) return 0;
  if (t >= to) return 1;
  return (t - from) / (to - from);
};

/** Easing curves used across all beats. Pure functions, no deps. */
export const easeOutCubic = (p) => 1 - Math.pow(1 - p, 3);
export const easeInOutCubic = (p) =>
  p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;

/** Render-only when `t` ∈ [start, end]; passes a 0..1 local progress. */
export const Beat = ({ start, end, children, fade = 0.25 }) => {
  const { t } = useTimeline();
  const active = t >= start - fade && t <= end + fade;
  if (!active) return null;
  const p = beatProgress(t, start, end);
  // Local in/out alpha so beats cross-fade cleanly at the boundaries.
  const inA = beatProgress(t, start - fade, start);
  const outA = 1 - beatProgress(t, end, end + fade);
  const alpha = Math.min(inA, outA);
  return (
    <div
      className="absolute inset-0"
      style={{ opacity: alpha, willChange: "opacity" }}
    >
      {typeof children === "function" ? children(p) : children}
    </div>
  );
};

const DURATION = 15.0; // seconds
const LOOP_GAP = 0.6; // black hold at end before looping

export default function ReelStage({ children, brandLine, accentColor = "#C9A227" }) {
  const [params] = useSearchParams();
  const orientation = params.get("o") === "horizontal" ? "horizontal" : "vertical";
  const clean = params.get("clean") === "1";
  const paused = params.get("paused") === "1";
  const frozenT = paused ? Math.max(0, Math.min(1, parseFloat(params.get("t") || "0"))) * DURATION : null;

  const W = orientation === "vertical" ? 1080 : 1920;
  const H = orientation === "vertical" ? 1920 : 1080;

  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [t, setT] = useState(frozenT ?? 0);
  const [cycle, setCycle] = useState(0);

  // Fit the locked-pixel stage into the actual viewport.
  useEffect(() => {
    const fit = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const s = Math.min(vw / W, vh / H);
      setScale(s);
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [W, H]);

  // Master clock — single rAF loop. 15s → loop.
  useEffect(() => {
    if (paused) return undefined;
    let raf = 0;
    let started = 0;
    const tick = (now) => {
      if (!started) started = now;
      const elapsed = (now - started) / 1000;
      const total = DURATION + LOOP_GAP;
      const localCycle = Math.floor(elapsed / total);
      const inCycle = elapsed - localCycle * total;
      setT(Math.min(inCycle, DURATION));
      setCycle(localCycle);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused]);

  // Imperative hook for backend MP4 rendering. When the page is in
  // `?paused=1` mode, we expose `window.__reelSetT(progress0to1)` so
  // a headless Playwright driver can step the timeline by simply
  // calling `page.evaluate('t => window.__reelSetT(t)', t)` instead of
  // navigating the URL per frame. That's the difference between a
  // ~150 s render and a ~30 s render, with crisper per-frame state.
  useEffect(() => {
    if (!paused) return undefined;
    window.__reelSetT = (progress) => {
      const clamped = Math.max(0, Math.min(1, Number(progress) || 0));
      setT(clamped * DURATION);
    };
    // Signal readiness so the Playwright driver knows it can start
    // calling __reelSetT instead of polling for the global to appear.
    window.__reelReady = true;
    return () => {
      delete window.__reelSetT;
      window.__reelReady = false;
    };
  }, [paused]);

  // 14.0–15.0s closing WaxSeal beat is rendered by ReelStage itself so
  // every reel resolves identically. Letterspacing-driven wordmark
  // reveal — slow-burn editorial.
  const closing = beatProgress(t, 13.4, 14.6);
  const closingOut = beatProgress(t, 14.8, 15.0);
  const closingActive = t >= 13.0;

  // Opening gold hairline swipe at the very top of every reel.
  const opening = beatProgress(t, 0.0, 1.4);

  return (
    <TimelineCtx.Provider value={{ t, cycle, orientation }}>
      <div
        className="fixed inset-0 bg-ink overflow-hidden flex items-center justify-center"
        data-testid="reel-shell"
      >
        <div
          ref={stageRef}
          className="relative bg-ink text-white origin-center"
          style={{
            width: W,
            height: H,
            transform: `scale(${scale})`,
            // Crisp font rendering on the downscaled stage.
            WebkitFontSmoothing: "antialiased",
            fontFamily: '"Manrope", -apple-system, sans-serif',
          }}
          data-testid={`reel-stage-${orientation}`}
        >
          {/* Cinematic grain + radial glow underlay applied to every reel. */}
          <NoiseOverlay opacity={0.07} />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background: `radial-gradient(circle at 78% 22%, ${accentColor}33, transparent 55%), radial-gradient(circle at 18% 82%, rgba(193,39,45,0.12), transparent 60%)`,
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at center, transparent 32%, rgba(0,0,0,0.85) 100%)",
            }}
          />

          {/* Top gold-hairline swipe — runs once at every loop start. */}
          <div
            aria-hidden="true"
            className="absolute top-0 left-0 h-[2px]"
            style={{
              width: `${easeOutCubic(opening) * 100}%`,
              background:
                "linear-gradient(90deg, transparent 0%, rgba(230,208,112,0.95) 35%, rgba(201,162,39,0.95) 65%, transparent 100%)",
              opacity: opening > 0 && opening < 1 ? 1 : 0,
              filter: "drop-shadow(0 0 12px rgba(201,162,39,0.45))",
            }}
          />

          {/* Beats supplied by the specific reel. */}
          {children}

          {/* Closing — WaxSeal medallion + wordmark + tagline */}
          {closingActive && (
            <ClosingFrame
              progress={closing}
              outro={closingOut}
              brandLine={brandLine}
              orientation={orientation}
            />
          )}

          {/* Recording marker — auto-hides via `?clean=1`. */}
          {!clean && (
            <div className="absolute bottom-6 right-6 flex items-center gap-2 text-[14px] tracking-widest text-white/35 font-mono">
              <span className="h-1.5 w-1.5 rounded-full bg-crimson animate-pulse" />
              {W}×{H} · 15s · loop
            </div>
          )}
        </div>
      </div>
    </TimelineCtx.Provider>
  );
}

function ClosingFrame({ progress, outro, brandLine, orientation }) {
  // Medallion scales in 0.4 → 1.0, halo pulses, wordmark reveals via
  // letter-spacing collapse. Outro fades the whole frame in the last
  // 0.2s so the loop seam is invisible.
  const mp = easeOutCubic(progress);
  const haloAlpha = 0.25 + Math.sin(progress * Math.PI) * 0.45;
  const sealSize = orientation === "vertical" ? 220 : 180;
  const wordmarkLetterSpacing = `${(1 - mp) * 1.2 + 0.06}em`;
  const taglineOpacity = Math.max(0, (progress - 0.55) / 0.45);
  const containerOpacity = 1 - outro;

  return (
    <div
      className="absolute inset-0 flex items-center justify-center"
      style={{ opacity: containerOpacity }}
    >
      {/* Halo */}
      <div
        aria-hidden="true"
        className="absolute"
        style={{
          width: sealSize * 4,
          height: sealSize * 4,
          background: `radial-gradient(circle at center, rgba(201,162,39,${haloAlpha}) 0%, transparent 50%)`,
          filter: "blur(8px)",
        }}
      />
      <div className="relative flex flex-col items-center text-center">
        <div
          style={{
            transform: `scale(${0.4 + mp * 0.6})`,
            transition: "none",
          }}
        >
          <WaxSeal size={sealSize} title="Armstrong K9 Registry" />
        </div>
        <h2
          className="font-spectral mt-12"
          style={{
            fontFamily: '"Spectral", Georgia, serif',
            fontWeight: 300,
            fontSize: orientation === "vertical" ? 72 : 64,
            letterSpacing: wordmarkLetterSpacing,
            color: "white",
            textShadow: "0 4px 24px rgba(0,0,0,0.6)",
          }}
        >
          ARMSTRONG K9
        </h2>
        <div
          className="mt-3 h-px"
          style={{
            width: 320 * mp,
            background:
              "linear-gradient(90deg, transparent, rgba(201,162,39,0.95) 50%, transparent)",
          }}
        />
        <p
          className="mt-5 uppercase tracking-[0.4em] text-[18px] text-gold-light"
          style={{
            opacity: taglineOpacity,
            transform: `translateY(${(1 - taglineOpacity) * 8}px)`,
          }}
        >
          The Registry
        </p>
        {brandLine && (
          <p
            className="mt-10 max-w-md text-[20px] leading-relaxed font-spectral italic text-white/85"
            style={{
              fontFamily: '"Spectral", Georgia, serif',
              opacity: taglineOpacity,
            }}
          >
            {brandLine}
          </p>
        )}
      </div>
    </div>
  );
}
